import React, { useEffect, useRef, useState, useContext } from "react";
import { Link } from "react-router-dom";
import "./aboutus.css";
import API from "../../Api/api";
import { MyContext } from "../../Context/MyContext";
import { withLocale } from "../../utils/localeStorage";
import localAboutUs from "../../json/aboutus"; // ← fallback data

const IconSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" className="mkt-ico-svg" aria-hidden="true">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
    <path d="M20 20l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconChevL = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconChevR = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconPlay = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="mkt-play">
    <path d="M8 5v14l11-7L8 5z" />
  </svg>
);

const IconStat1 = () => (
  <svg viewBox="0 0 48 48" fill="none" className="mkt-conv-svg" aria-hidden="true">
    <rect x="8" y="10" width="32" height="30" rx="3" stroke="var(--color-primary)" strokeWidth="1.8" />
    <path d="M8 18h32" stroke="var(--color-info)" strokeWidth="1.8" />
    <circle cx="18" cy="28" r="2" fill="var(--color-primary)" />
  </svg>
);
const IconStat2 = () => (
  <svg viewBox="0 0 48 48" fill="none" className="mkt-conv-svg" aria-hidden="true">
    <circle cx="16" cy="18" r="5" stroke="var(--color-primary)" strokeWidth="1.8" />
    <path d="M8 38c0-6 4-10 8-10s8 4 8 10" stroke="var(--color-info)" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="32" cy="16" r="4" stroke="var(--color-info)" strokeWidth="1.8" />
    <path d="M28 38h12" stroke="var(--color-primary)" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);
const IconStat3 = () => (
  <svg viewBox="0 0 48 48" fill="none" className="mkt-conv-svg" aria-hidden="true">
    <path d="M10 38V18l14-8 14 8v20" stroke="var(--color-primary)" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M18 38V26h12v12" stroke="var(--color-info)" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);
const IconStat4 = () => (
  <svg viewBox="0 0 48 48" fill="none" className="mkt-conv-svg" aria-hidden="true">
    <circle cx="24" cy="24" r="14" stroke="var(--color-primary)" strokeWidth="1.8" />
    <path d="M16 20c3-4 13-4 16 0M16 28c3 4 13 4 16 0" stroke="var(--color-info)" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const CONV_ICONS = [IconStat1, IconStat2, IconStat3, IconStat4];

const AboutUs = () => {
  const { locale } = useContext(MyContext);
  const [aboutus, setAboutus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/aboutus', withLocale(locale))
      .then(res => {
        // Use API data if available, otherwise fall back to local JSON
        if (res.data && res.data.length > 0) {
          setAboutus(res.data[0]);
        } else {
          setAboutus(localAboutUs);
        }
      })
      .catch(() => {
        // API unavailable or DB empty — use local JSON so the page still renders
        setAboutus(localAboutUs);
      })
      .finally(() => setLoading(false));
  }, [locale]);

  const [videoOpen, setVideoOpen] = useState(false);
  const rootRef = useRef(null);
  const cardsRef = useRef(null);

  useEffect(() => {
    if (loading || !aboutus) return;
    const root = rootRef.current;
    if (!root) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("mkt-in")),
      { threshold: 0.12 }
    );
    root.querySelectorAll(".mkt-reveal").forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [loading, aboutus]); // ← added proper deps so observer runs after data loads

  const scrollCards = (dir) => {
    const el = cardsRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.min(360, el.clientWidth * 0.85), behavior: "smooth" });
  };

  if (loading) return (
    <div className="mkt" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
      Loading...
    </div>
  );

  // aboutus is always set (either from API or local JSON), so this is a true safety net only
  if (!aboutus) return null;

  const {
    hero = {},
    heroBar = [],
    tagline = "",
    story = {},
    milestonesSection = {},
    milestones = [],
    statsSection = {},
    stats = [],
    video = {},
    cta = {},
  } = aboutus;

  return (
    <div className="mkt" ref={rootRef}>
      {/* Hero */}
      <section className="mkt-hero">
        <div className="mkt-hero-bg">
          {hero?.image && <img src={hero.image} alt="" className="mkt-hero-img" loading="eager" />}
          <div className="mkt-hero-shade" aria-hidden="true" />
        </div>
        <div className="mkt-hero-inner">
          <p className="mkt-hero-brand mkt-reveal">{hero?.eyebrow}</p>
          <h1 className="mkt-hero-title mkt-reveal">{hero?.title}</h1>
          <p className="mkt-hero-sub mkt-reveal">{hero?.subtitle}</p>

          <div className="mkt-hero-bar mkt-reveal" role="region" aria-label="Quick facts">
            {heroBar?.map((seg, i) => (
              <div key={i} className="mkt-hero-bar-seg">
                <span className="mkt-hero-bar-label">{seg.label}</span>
                <span className="mkt-hero-bar-value">{seg.value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestone cards */}
      <section className="mkt-section mkt-section--cards" aria-labelledby="mkt-cards-h">
        <div className="mkt-container">
          <div className="mkt-row-head mkt-reveal">
            <div>
              <h2 id="mkt-cards-h" className="mkt-section-title">
                {milestonesSection?.title}
              </h2>
              <p className="mkt-section-sub">{milestonesSection?.lead}</p>
            </div>
            {/* <div className="mkt-arrows">
              <button type="button" className="mkt-arrow mkt-arrow--ghost" onClick={() => scrollCards(-1)} aria-label="Previous">
                <IconChevL />
              </button>
              <button type="button" className="mkt-arrow mkt-arrow--solid" onClick={() => scrollCards(1)} aria-label="Next">
                <IconChevR />
              </button>
            </div> */}
          </div>

          <div className="mkt-cards-wrap mkt-reveal">
            <ul className="mkt-cards" ref={cardsRef}>
              {milestones?.map((m, i) => (
                <li key={`${m.year}-${i}`} className="mkt-card">
                  <div className="mkt-card-img-wrap">
                    {m.image && <img src={m.image} alt="" loading="lazy" />}
                    <span className="mkt-card-badge">{m.year}</span>
                  </div>
                  <div className="mkt-card-body">
                    <h3 className="mkt-card-title">{m.title}</h3>
                    <p className="mkt-card-meta">{story?.badge}</p>
                    <p className="mkt-card-desc">{m.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="mkt-section mkt-section--conv" aria-labelledby="mkt-conv-h">
        <div className="mkt-container">
          <div className="mkt-conv-head mkt-reveal">
            <h2 id="mkt-conv-h" className="mkt-section-title mkt-section-title--split">
              {statsSection?.title}
            </h2>
            <p className="mkt-conv-text">{statsSection?.subtitle}</p>
          </div>
          <ul className="mkt-conv-grid mkt-reveal">
            {stats?.map((s, i) => {
              const Ico = CONV_ICONS[i % CONV_ICONS.length];
              return (
                <li key={s.id || i} className="mkt-conv-item">
                  <Ico />
                  <span className="mkt-conv-val">{s.value}</span>
                  <span className="mkt-conv-lbl">{s.label}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Story */}
      <section className="mkt-section mkt-section--story" aria-labelledby="mkt-story-h">
        <div className="mkt-container mkt-story-split mkt-reveal">
          <div>
            <h2 id="mkt-story-h" className="mkt-section-title">
              {story?.title}
            </h2>
            <p className="mkt-story-tag">{tagline}</p>
          </div>
          <p className="mkt-story-body">{story?.body}</p>
        </div>
      </section>

      {/* Video */}
      <section className="mkt-section mkt-section--vid" aria-labelledby="mkt-vid-h">
        <div className="mkt-container">
          <h2 id="mkt-vid-h" className="mkt-section-title mkt-reveal">
            {video?.title}
          </h2>
          <p className="mkt-section-sub mkt-reveal">{video?.caption}</p>
          <button
            type="button"
            className="mkt-vid mkt-reveal"
            onClick={() => setVideoOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={videoOpen}
          >
            {video?.thumbnail && <img src={video.thumbnail} alt="" loading="lazy" />}
            <span className="mkt-vid-dim" aria-hidden="true" />
            <span className="mkt-vid-play">
              <IconPlay />
            </span>
          </button>
          <p className="mkt-cta-line mkt-reveal">{cta?.line}</p>
          <Link to={cta?.path || '#'} className="mkt-cta-btn mkt-reveal">
            {cta?.label}
          </Link>
        </div>
      </section>

      {videoOpen && (
        <div
          className="mkt-modal"
          role="dialog"
          aria-modal="true"
          aria-label={video?.title || 'Video'}
          onClick={() => setVideoOpen(false)}
        >
          <div className="mkt-modal-box" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="mkt-modal-x" onClick={() => setVideoOpen(false)} aria-label="Close">
              ×
            </button>
            <div className="mkt-modal-frame">
              <iframe src={video?.embedUrl} title={video?.title} allowFullScreen />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutUs;